import { BrandGroupConfig } from '../types/apiConfig';

// Hosts outside the brand-group config that prd clients and observed ext-location values use.
export const EXTRA_RETURN_URL_DOMAINS = [
  'mediahuis.com',
  'mediahuisgroup.com',
  'nrc.nl',
  'vrouwglossy.nl',
  'supersonntag.de',
  'zeitungsverlag-aachen.de',
  'qualifio.com',
  'qualifioapp.com',
];

const NATIVE_CALLBACK_DOMAINS = ['twipecloud.net'];
const WEB_SCHEMES = [
  'http:',
  'https:',
  'javascript:',
  'data:',
  'vbscript:',
  'file:',
  'blob:',
  'about:',
  'ftp:',
  'ws:',
  'wss:',
  'intent:',
];

export function allowedReturnUrlDomains(brandGroups: BrandGroupConfig[]): string[] {
  return [
    ...brandGroups.flatMap(({ brands }) =>
      brands.flatMap(({ urlDisplay }) => (urlDisplay ? [urlDisplay.toLowerCase()] : []))
    ),
    ...EXTRA_RETURN_URL_DOMAINS,
  ];
}

export type ReturnUrlPolicy = { domains: string[]; devDomains: string[] };

export function returnUrlPolicy(
  brandGroups: BrandGroupConfig[],
  devDomains: string[] = []
): ReturnUrlPolicy {
  return {
    domains: allowedReturnUrlDomains(brandGroups),
    devDomains: devDomains.map((domain) => domain.toLowerCase()),
  };
}

const isOnDomain = (host: string, domains: string[]) =>
  domains.some((domain) => host === domain || host.endsWith(`.${domain}`));

const isDevUrl = (url: URL, devDomains: string[]) =>
  (url.protocol === 'http:' || url.protocol === 'https:') && isOnDomain(url.hostname, devDomains);

export function toAllowedReturnUrl(value: string | null, policy: ReturnUrlPolicy): string | null {
  if (!value) return null;

  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }

  if (isDevUrl(url, policy.devDomains)) return value;
  if (url.protocol !== 'https:') return null;
  return isOnDomain(url.hostname, policy.domains) ? value : null;
}

// OAuth callbacks: web clients use allowlisted https, native apps use their own URI scheme.
export function toAllowedRedirectUri(
  value: string | null | undefined,
  policy: ReturnUrlPolicy
): string | null {
  if (!value) return null;

  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }

  if (isDevUrl(url, policy.devDomains)) return value;
  if (url.protocol === 'https:') {
    return toAllowedReturnUrl(value, {
      ...policy,
      domains: [...policy.domains, ...NATIVE_CALLBACK_DOMAINS],
    });
  }
  return WEB_SCHEMES.includes(url.protocol) ? null : value;
}

export function toRegistrationRedirectUri(
  value: string | null | undefined,
  policy: ReturnUrlPolicy
): string | undefined {
  if (value === '') return '';
  return toAllowedRedirectUri(value, policy) ?? undefined;
}
